import { useState, useEffect } from 'react';

interface Candle {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface TradingChartProps {
  pair: string;
}

export default function TradingChart({ pair }: TradingChartProps) {
  const [chartTimeframe, setChartTimeframe] = useState('1H');
  const [candles, setCandles] = useState<Candle[]>([]);
  const [currentPrice, setCurrentPrice] = useState(0);

  useEffect(() => {
    const generateCandles = () => {
      const data: Candle[] = [];
      let basePrice = pair === 'BTC/USDT' ? 43250 : pair === 'ETH/USDT' ? 2280 : 0.003;
      const now = Date.now();

      for (let i = 50; i >= 0; i--) {
        const time = new Date(now - i * 3600000).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        });
        const open = basePrice;
        const changePercent = pair === 'PEP/USDT' ? 0.02 : 0.005;
        const change = (Math.random() - 0.5) * basePrice * changePercent;
        const close = open + change;
        const high = Math.max(open, close) * (1 + Math.random() * 0.01);
        const low = Math.min(open, close) * (1 - Math.random() * 0.01);
        const volume = Math.random() * 50000 + 10000;

        data.push({ time, open, high, low, close, volume });
        basePrice = close;
      }

      return data;
    };

    const initialCandles = generateCandles();
    setCandles(initialCandles);
    setCurrentPrice(initialCandles[initialCandles.length - 1]?.close || 0);

    // Real-time price updates
    const interval = setInterval(() => {
      setCandles(prev => {
        const lastCandle = prev[prev.length - 1];
        if (!lastCandle) return prev;

        const changePercent = pair === 'PEP/USDT' ? 0.02 : 0.005;
        const change = (Math.random() - 0.5) * lastCandle.close * changePercent;
        const newClose = lastCandle.close + change;
        const newHigh = Math.max(lastCandle.high, newClose);
        const newLow = Math.min(lastCandle.low, newClose);

        const updatedCandle = {
          ...lastCandle,
          close: newClose,
          high: newHigh,
          low: newLow,
        };

        setCurrentPrice(newClose);
        return [...prev.slice(0, -1), updatedCandle];
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [pair]);

  // Calculate price statistics
  const maxPrice = Math.max(...candles.map((c) => c.high));
  const minPrice = Math.min(...candles.map((c) => c.low));
  const firstPrice = candles[0]?.close ?? currentPrice;
  const lastPrice = candles[candles.length - 1]?.close ?? currentPrice;
  const priceChange = firstPrice ? ((lastPrice - firstPrice) / firstPrice) * 100 : 0;

  const priceRange = maxPrice - minPrice;
  const maxVolume = Math.max(...candles.map((c) => c.volume));

  const getCandleY = (price: number) => {
    return ((maxPrice - price) / priceRange) * 200;
  };

  const getVolumeHeight = (volume: number) => {
    return (volume / maxVolume) * 60;
  };

  const formatPrice = (price: number) => {
    if (pair === 'PEP/USDT') return price.toFixed(4);
    return price.toFixed(2);
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-xl border border-cyan-500/20 p-6 shadow-2xl shadow-cyan-500/10">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-1">
            {pair} Chart
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-2xl font-mono font-bold text-white">
              ${formatPrice(currentPrice)}
            </span>
            <span className={`text-sm font-mono px-2 py-1 rounded ${
              priceChange >= 0 
                ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                : 'bg-red-500/20 text-red-400 border border-red-500/30'
            }`}>
              {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {['15M', '1H', '4H', '1D', '1W'].map((tf) => (
            <button
              key={tf}
              onClick={() => setChartTimeframe(tf)}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono font-bold transition-all cursor-pointer whitespace-nowrap ${
                chartTimeframe === tf
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/50'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-700'
              }`}
            >
              {tf}
            </button>
          ))}
        </div>
      </div>

      {/* Chart Area */}
      <div className="relative bg-black/40 rounded-lg p-4 border border-cyan-500/10 overflow-x-auto">
        <div className="min-w-[600px]">
          {/* Price Scale */}
          <div className="absolute left-0 top-0 bottom-16 w-20 flex flex-col justify-between text-xs font-mono text-cyan-400/80">
            <span className="bg-gray-900/80 px-2 py-1 rounded">${formatPrice(maxPrice)}</span>
            <span className="bg-gray-900/80 px-2 py-1 rounded">${formatPrice((maxPrice + minPrice) / 2)}</span>
            <span className="bg-gray-900/80 px-2 py-1 rounded">${formatPrice(minPrice)}</span>
          </div>

          <div className="ml-20 mr-4">
            {/* Grid Lines */}
            <svg width="100%" height="280" className="overflow-visible">
              <defs>
                <linearGradient id="gridGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.1" />
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.02" />
                </linearGradient>
              </defs>

              {/* Horizontal Grid Lines */}
              {[0, 1, 2, 3, 4].map((i) => (
                <line
                  key={i}
                  x1="0"
                  y1={i * 50}
                  x2="100%"
                  y2={i * 50}
                  stroke="#06b6d4"
                  strokeWidth="1"
                  strokeOpacity="0.1"
                  strokeDasharray="4 4"
                />
              ))}

              {/* Candlesticks */}
              {candles.map((candle, index) => {
                const x = (index / candles.length) * 100;
                const candleWidth = (100 / candles.length) * 0.6;
                const isGreen = candle.close >= candle.open;
                const color = isGreen ? '#10b981' : '#ef4444';
                const glowColor = isGreen ? '#10b981' : '#ef4444';

                const highY = getCandleY(candle.high);
                const lowY = getCandleY(candle.low);
                const openY = getCandleY(candle.open);
                const closeY = getCandleY(candle.close);
                const bodyTop = Math.min(openY, closeY);
                const bodyHeight = Math.abs(closeY - openY);

                return (
                  <g key={index}>
                    {/* Wick */}
                    <line
                      x1={`${x}%`}
                      y1={highY}
                      x2={`${x}%`}
                      y2={lowY}
                      stroke={color}
                      strokeWidth="2"
                      opacity="0.8"
                    />
                    {/* Body */}
                    <rect
                      x={`${x - candleWidth / 2}%`}
                      y={bodyTop}
                      width={`${candleWidth}%`}
                      height={Math.max(bodyHeight, 2)}
                      fill={color}
                      opacity="0.9"
                      filter={`drop-shadow(0 0 4px ${glowColor})`}
                    />
                  </g>
                );
              })}

              {/* Current Price Line */}
              <line
                x1="0"
                y1={getCandleY(currentPrice)}
                x2="100%"
                y2={getCandleY(currentPrice)}
                stroke="#06b6d4"
                strokeWidth="2"
                strokeDasharray="8 4"
                opacity="0.6"
              />
            </svg>

            {/* Volume Bars */}
            <div className="mt-2 flex items-end gap-[2px] h-16 bg-gradient-to-t from-gray-900/50 to-transparent rounded">
              {candles.map((candle, index) => {
                const isGreen = candle.close >= candle.open;
                const height = getVolumeHeight(candle.volume);

                return (
                  <div
                    key={index}
                    className={`flex-1 rounded-t transition-all ${
                      isGreen 
                        ? 'bg-gradient-to-t from-green-500/40 to-green-500/20' 
                        : 'bg-gradient-to-t from-red-500/40 to-red-500/20'
                    }`}
                    style={{ height: `${height}px` }}
                  />
                );
              })}
            </div>

            {/* Time Labels */}
            <div className="flex justify-between mt-2 text-xs font-mono text-cyan-400/60">
              {candles.filter((_, i) => i % 10 === 0).map((candle, index) => (
                <span key={index}>{candle.time}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
        <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20 rounded-lg p-3">
          <div className="text-xs font-mono text-green-400/60 mb-1">24h High</div>
          <div className="text-sm font-mono font-bold text-green-400">
            ${formatPrice(maxPrice)}
          </div>
        </div>
        <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 border border-red-500/20 rounded-lg p-3">
          <div className="text-xs font-mono text-red-400/60 mb-1">24h Low</div>
          <div className="text-sm font-mono font-bold text-red-400">
            ${formatPrice(minPrice)}
          </div>
        </div>
        <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/20 rounded-lg p-3">
          <div className="text-xs font-mono text-cyan-400/60 mb-1">24h Volume</div>
          <div className="text-sm font-mono font-bold text-cyan-400">
            {candles.length > 0 ? (candles.reduce((sum, c) => sum + c.volume, 0) / 1000).toFixed(1) : '0.0'}K
          </div>
        </div>
        <div className={`bg-gradient-to-br rounded-lg p-3 border ${
          priceChange >= 0 
            ? 'from-green-500/10 to-green-500/5 border-green-500/20' 
            : 'from-red-500/10 to-red-500/5 border-red-500/20'
        }`}>
          <div className={`text-xs font-mono mb-1 ${
            priceChange >= 0 ? 'text-green-400/60' : 'text-red-400/60'
          }`}>
            24h Change
          </div>
          <div className={`text-sm font-mono font-bold ${
            priceChange >= 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
          </div>
        </div>
      </div>
    </div>
  );
}
